// MenschenFuersUnternehmenDialog.tsx — Refined-Modal für Verantwortlichkeiten
// (Geschäftsführer/Gesellschafter + Verantwortlichkeiten) über EIN
// VerantwortlichkeitInput-Popup. Zwei Perspektiven + Add/Edit:
//   • company  (Firma-Detail „Menschen fürs Unternehmen"): feste Firma, Mensch wird
//     gewählt (Person/Firma).
//   • person   (Person-Detail „Verbindungen zu Firmen"): fester Mensch (= aktueller
//     Person-Kontakt), Firma wird gewählt.
//   • mode 'add'  → additiv anhängen.
//   • mode 'edit' → vorbefüllt; Speichern ERSETZT den Satz (attach-menschen replace).
// Speichert transaktional über POST ansprechpartner-cleanup/attach-menschen
// (Status „Verantwortung fürs Unternehmen", FO/BO-Vererbung, Relationen — server-seitig).

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { ContactRefField } from "../relationsInline/ContactRefField";
import { useFirmenZentralen } from "../forms/widgets/firmenZentrale";
import { createContactFromRef } from "../relationsInline/persistRelations";
import { VollanlageElternContext, type VollanlageEltern } from "../forms/VollanlageWeiter";
import { refDisplayName, type ContactRef } from "../relationsInline/types";
import {
  VerantwortlichkeitInput,
  type VerantwortlichkeitValue,
} from "../verantwortlichkeit/VerantwortlichkeitInput";
import { buildMenschenItems } from "../verantwortlichkeit/buildMenschenItems";
import { attachMenschen } from "../../services/menschenFuersUnternehmenService";
import { useUnternehmensBegriff, verantwortungTitel, fuerDasUnternehmen } from "../../utils/unternehmensBegriff";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

const EMPTY_VERANT: VerantwortlichkeitValue = { isExtern: false, roles: [], externalCompany: null };

export function MenschenFuersUnternehmenDialog({
  companyId,
  companyLegalFormId,
  perspective = "company",
  mode = "add",
  kontext = "firma",
  fixedMensch,
  festeFirma,
  lockedRef,
  initialVerant,
  onClose,
  onSaved,
}: {
  /**
   * Firma-Id. company-Perspektive: die feste Firma (Owner der FO/BO-Vererbung).
   * person-Perspektive im Edit: die feste Firma. person-Perspektive im Add:
   * undefined (Firma wird im Dialog gewählt).
   */
  companyId?: number;
  /**
   * Rechtsform der festen Firma. Sie leistet zweierlei: Sie entscheidet, welche
   * Organrollen wählbar sind (harte Filterung über die Matrix, PO 15.08.2026),
   * und sie benennt die Überschrift („Verantwortung für den Verein"). Im
   * person-Add wird sie stattdessen aus der gewählten Firma gelesen; ohne Angabe
   * bleibt es beim Default „Unternehmen".
   */
  companyLegalFormId?: number;
  perspective?: "company" | "person";
  mode?: "add" | "edit";
  /**
   * Nur Beschriftung, nur in der person-Perspektive: Der Gegenkontakt trägt ein
   * eigenes `verbindungLabel` — Verein, Stiftung, Genossenschaft, Gemeinschaft
   * oder Körperschaft. Alle sind datenmodellhaft eine Firma; „Verbindung zu Firma
   * hinzufügen" über einer Vereinsmitgliedschaft oder einem Miteigentumsanteil
   * wäre trotzdem falsch. Am Gespeicherten ändert das nichts.
   */
  kontext?: "firma" | "vereinGemeinschaft";
  /** person-Perspektive: der aktuelle Person-Kontakt (der „Mensch", fest). */
  fixedMensch?: ContactRef;
  /**
   * person-Add mit schon feststehender Firma — der Weg aus dem
   * Arbeitgeber-Feld: Dort ist die Firma gerade gewählt oder angelegt worden,
   * und der Dialog fragt nur noch die Rolle ab. Die Firmenauswahl steht dann
   * als Anzeige da, nicht als Suchfeld.
   */
  festeFirma?: ContactRef;
  /** Edit: der zu bearbeitende Gegenkontakt (company: Person; person: Firma) — nur Anzeige. */
  lockedRef?: ContactRef;
  /** Edit: Prefill der bestehenden Verantwortlichkeiten/Rollen. */
  initialVerant?: VerantwortlichkeitValue;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = mode === "edit";
  const isPerson = perspective === "person";
  // company-Add: Mensch (Person/Firma) wählen. person-Add: Firma wählen.
  const [picked, setPicked] = React.useState<ContactRef | null>(festeFirma ?? null);
  const [verant, setVerant] = React.useState<VerantwortlichkeitValue>(initialVerant ?? EMPTY_VERANT);
  const [saving, setSaving] = React.useState(false);
  // company-Add: Die Firma steht fest — ihre Telefon-/Faxnummern sind die
  // Zentrale für die Durchwahl der geschäftlichen Nummer einer neuen Person.
  const firmenZentrale = useFirmenZentralen(!isPerson && companyId ? [companyId] : []);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    alleFenster.addEventListener("keydown", onKey);
    return () => alleFenster.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Effektive Firma-Id + „Mensch"-Ref je Perspektive/Modus auflösen.
  const effCompanyId = isPerson
    ? isEdit
      ? companyId
      : picked?.contactId // person-Add: gewählte (bestehende) Firma = companyId
    : companyId; // company: feste Firma
  const menschRef: ContactRef | null = isPerson
    ? fixedMensch ?? null // person: der aktuelle Kontakt ist der Mensch
    : isEdit
      ? lockedRef ?? null // company-Edit: die zu bearbeitende Person (fest)
      : picked; // company-Add: gewählter Mensch

  // person-Add: die Firma ist frei eingetippt, existiert also noch nicht.
  const neueFirma = isPerson && !isEdit && !!picked && !picked.contactId;

  // Rechtsform der Firma, um die es geht: in der person-Perspektive im Add die
  // gerade gewählte/eingetippte Firma, sonst die feste Firma des Aufrufers.
  const effLegalFormId = isPerson && !isEdit ? picked?.legalFormId : companyLegalFormId;

  async function save() {
    if (!menschRef || (!menschRef.contactId && !menschRef.lastName?.trim())) {
      setError("Bitte eine Person oder Firma wählen.");
      return;
    }
    // Die Rollen-Pflicht ist mit dem Haken gefallen (PO 15.08.2026): Ein
    // Mitarbeiter OHNE jede Verantwortlichkeit ist genau sein Zweck — er ist die
    // Grundlage für bAV/bKV. Leer bleiben darf die Verbindung trotzdem nicht.
    if (!verant.roles.length && !verant.istMenschImUnternehmen) {
      setError("Bitte eine Verantwortlichkeit wählen oder „Mensch im Unternehmen“ ankreuzen.");
      return;
    }
    // Ohne Rechtsform lässt sich später keine rechtsformabhängige Rolle prüfen —
    // sie wird deshalb hier mitgesetzt statt in einem zweiten Schritt nachgepflegt.
    if (neueFirma && !picked?.legalFormId && !picked?.legalForm?.trim()) {
      setError("Bitte die Rechtsform der neuen Firma wählen.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      // Die neue Firma muss angelegt sein, bevor attach-menschen sie als
      // companyId verankern kann — der Endpoint legt nur den „Menschen" an,
      // nie die Firma.
      let companyId = effCompanyId;
      if (!companyId && neueFirma && picked) {
        // C2 (Plan 2026-09-30): Die neue Firma erbt FO/BO der Person, aus
        // deren Akte sie entsteht.
        companyId = await createContactFromRef(picked, {}, undefined, menschRef.contactId);
      }
      if (!companyId || companyId <= 0) {
        setError(isPerson ? "Bitte eine Firma wählen oder neu eintippen." : "Keine Firma zugeordnet.");
        return;
      }
      const items = buildMenschenItems([{ ref: menschRef, verant }], companyId);
      if (items.length) await attachMenschen(items, isEdit);
      onSaved();
      onClose();
    } catch {
      setError("Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  // Der Begriff folgt der Rechtsform der festen Firma (company-Perspektive). Die
  // person-Perspektive behält ihre Überschriften: dort heißt die Sektion
  // „Verbindungen zu Firmen" und nennt gar kein Unternehmen.
  const begriff = useUnternehmensBegriff(isPerson ? undefined : companyLegalFormId);

  // person-Perspektive: Der Abschnitt, aus dem der Dialog aufgerufen wurde,
  // heißt entweder „Verbindungen zu Firmen" oder „Vereine und Gemeinschaften" —
  // die Überschriften folgen ihm. Bewusst NICHT „Mitgliedschaft hinzufügen":
  // Die Mehrheit der Fälle sind Eigentümergemeinschaften, und dort ist die
  // Verbindung ein Miteigentumsanteil, keine Mitgliedschaft und kein Amt.
  const istVereinGemeinschaft = isPerson && kontext === "vereinGemeinschaft";
  const kicker = isPerson
    ? istVereinGemeinschaft
      ? "Vereine und Gemeinschaften"
      : "Verbindungen zu Firmen"
    : verantwortungTitel(begriff);
  const title = isEdit
    ? istVereinGemeinschaft
      ? "Rolle bearbeiten"
      : "Verantwortlichkeiten bearbeiten"
    : isPerson
      ? istVereinGemeinschaft
        ? "Verbindung zu Verein oder Gemeinschaft hinzufügen"
        : "Verbindung zu Firma hinzufügen"
      : `Mensch ${fuerDasUnternehmen(begriff)} hinzufügen`;
  const pickerLabel = isPerson
    ? istVereinGemeinschaft
      ? "Verein oder Gemeinschaft"
      : "Firma"
    : "Person oder Firma";
  // Gesperrte Gegenkontakt-Anzeige (Edit) bzw. fester Mensch (person-Perspektive-Info).
  const lockedName = isEdit ? refDisplayName(lockedRef ?? null) : "";

  // „+ Weitere Informationen erfassen": Der voll angelegte Kontakt erbt FO/BO
  // des Kontakts, aus dessen Akte der Dialog kommt — wie die Schnellanlage.
  const vollanlageEltern = React.useMemo<VollanlageEltern>(
    () => ({
      elternId: isPerson ? fixedMensch?.contactId : companyId,
      elternName: isPerson && fixedMensch ? refDisplayName(fixedMensch) : undefined,
    }),
    [isPerson, fixedMensch, companyId],
  );

  return createPortal(
    <div className="kvp-rd kvp-rd-scrim" onClick={onClose}>
      <div
        className="dm"
        role="dialog"
        aria-modal="true"
        style={{ width: "min(560px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">{kicker}</div>
              <h2>{title}</h2>
            </div>
            <button className="dm-x" type="button" onClick={onClose} aria-label="Schließen">
              <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          </div>
        </div>
        <div className="dm-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="fld">
            <label>{pickerLabel}</label>
            {isEdit ? (
              // Gegenkontakt fix — nur Anzeige, nicht änderbar.
              <input className="inp" value={lockedName} readOnly />
            ) : isPerson && festeFirma ? (
              // Die Firma steht schon fest (Arbeitgeber-Feld) — nur Anzeige.
              <input
                className="inp"
                value={festeFirma.displayName ?? festeFirma.lastName ?? ""}
                readOnly
              />
            ) : isPerson ? (
              // person-Add: Firma wählen — bestehend ODER neu eintippen.
              // `firmaExtraFields` bringt Rechtsform und Branche mit; ohne sie
              // entstand eine Firma ohne Rechtsform (und die Erkennung aus dem
              // Firmennamen lief gar nicht erst an).
              <VollanlageElternContext.Provider value={vollanlageEltern}>
              <ContactRefField
                wantType="Firma"
                firmaExtraFields
                idBase="mfu-dialog-firma"
                value={picked}
                onChange={setPicked}
                suchPlaceholder={
                  istVereinGemeinschaft ? "Verein, Stiftung oder Gemeinschaft suchen …" : undefined
                }
              />
              </VollanlageElternContext.Provider>
            ) : (
              // company-Add: Person ODER Firma als Mensch wählen.
              <VollanlageElternContext.Provider value={vollanlageEltern}>
              <ContactRefField
                wantType="Person"
                allowTypeSwitch
                firmaExtraFields
                idBase="mfu-dialog-p"
                zentrale={firmenZentrale}
                value={picked}
                onChange={setPicked}
              />
              </VollanlageElternContext.Provider>
            )}
          </div>
          <VerantwortlichkeitInput
            idBase="mfu-dialog-v"
            value={verant}
            onChange={setVerant}
            legalFormId={effLegalFormId}
            gegenkontaktIstFirma={menschRef?.type === "Firma"}
          />
          {error && (
            <div style={{ color: "var(--danger)", font: "600 13.5px/1.4 var(--ui)" }}>{error}</div>
          )}
        </div>
        <div className="dm-foot">
          <button type="button" className="rd-pop-btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rd-pop-btn primary" onClick={save} disabled={saving}>
            {saving ? "Speichern …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
